import { computed, inject, Injectable } from '@angular/core';
import { combineLatest, filter, mergeMap, Observable, take } from 'rxjs';
import {
  ApiAssessmentResponse,
  ApiDiagramResponse,
  ApiProgressResponse,
  ApiTargetResponse,
  ApiTransitionElementResponse,
  ApiWatchlistResponse,
  ModelsTargetType,
} from '../../api/core/models';
import { TransitionElementService } from '../../api/core/services';
import { BoardFacadeService } from '../../services/store/board/board-facade.service';
import { TransitionElementsStore } from './transition-elements.store';
@Injectable({
  providedIn: 'root',
})
export class TransitionElementsFacadeService {
  private readonly _boardFacadeService = inject(BoardFacadeService);
  private readonly _transitionElementsService = inject(TransitionElementService);
  private readonly _transitionElementsStore = inject(TransitionElementsStore);

  error = this._transitionElementsStore.error;
  isLoading = this._transitionElementsStore.isLoading;
  transitionElements = this._transitionElementsStore.transitionElements;

  getTransitionElement(transitionElementId: string, scenarioId?: string): Observable<ApiTransitionElementResponse> {
    return combineLatest([
      this._boardFacadeService.getBoardProperty$(['id']),
      this._boardFacadeService.boardLanguage$,
    ]).pipe(
      filter(([boardId, boardLang]) => Boolean(boardId) && Boolean(boardLang)),
      take(1),
      mergeMap(([boardId, boardLang]) =>
        this._transitionElementsService.getTransitionElement({
          id: transitionElementId,
          boardId,
          scenarioId,
          'Accept-Language': boardLang,
        })
      )
    );
  }

  getTransitionElements(scenarioId?: string) {
    // don't load transition elements if they are already loaded, or if scenarioId is not provided
    // this method is called from scenarios store when a new scenario is selected
    if (!this._transitionElementsStore.transitionElements() && scenarioId) {
      this._transitionElementsStore.loadTransitionElements({ scenarioId });
    }
    return computed(() => this._transitionElementsStore.transitionElements());
  }

  updateTransitionElement(transitionElementId: string, targetType: ModelsTargetType) {
    this._transitionElementsStore.updateTransitionElement({ transitionElementId, targetType });
  }

  updateTransitionElementAssessment(transitionElementId: string, assessment: ApiAssessmentResponse) {
    this._transitionElementsStore.updateTransitionElementAssessment({ transitionElementId, assessment });
  }

  updateTransitionElementDiagram(transitionElementId: string, diagram: ApiDiagramResponse) {
    this._transitionElementsStore.updateTransitionElementDiagram({ transitionElementId, diagram });
  }

  updateTransitionElementOtherScenarioTargets(
    transitionElementId: string,
    targets: { [key: string]: ApiTargetResponse }
  ) {
    this._transitionElementsStore.updateTransitionElementOtherScenarioTargets({ transitionElementId, targets });
  }

  updateTransitionElementProgress(transitionElementId: string, progress: ApiProgressResponse) {
    this._transitionElementsStore.updateTransitionElementProgress({ transitionElementId, progress });
  }

  updateTransitionElementTarget(transitionElementId: string, target: ApiTargetResponse) {
    this._transitionElementsStore.updateTransitionElementTarget({ transitionElementId, target });
  }

  updateTransitionElementWatchlist(
    transitionElementId: string,
    watchlist: ApiWatchlistResponse,
    shouldDeleteWatchlist: boolean
  ) {
    this._transitionElementsStore.updateTransitionElementWatchlist({
      transitionElementId,
      watchlist,
      shouldDeleteWatchlist,
    });
  }
}
